import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import ProductConstants, { productDimensions } from '../../common/ProductConstants';
import LeftChevron from '../../assets/svg/LeftChevron.svg';
import RightChevron from '../../assets/svg/RightChevron.svg';
import * as styles from './ProductOptionPills.css';

const cx = classNames.bind(styles);

function ProductOptionPills({
    productOptions,
    onOptionClick,
    selectedOption,
    clientRendering = false,
    customOptions,
    checkSizeWarning,
    extendedSizeDetails = [],
    congruentPPsDetails = [],
    isMobile = false,
}) {
    let listRef = useRef(null);
    const [isRightOverflow, setIsRightOverflow] = useState(false);
    const [isLeftOverflow, setIsLeftOverflow] = useState(false);
    const inLandscapeMode =
        !__SERVER__ && window && Math.abs(window.orientation) === 90 && window.innerWidth >= 767;

    /* istanbul ignore next */
    useEffect(() => {
        const scrollableList = listRef;

        if (scrollableList && isMobile && congruentPPsDetails?.length > 0) {
            updateChevronVisiility(scrollableList);
            scrollableList.addEventListener('scroll', () => updateChevronVisiility(scrollableList));
            scrollableList.addEventListener('scrollend', () => resetScrollingList(scrollableList));

            return () => {
                scrollableList.removeEventListener('scroll', () =>
                    updateChevronVisiility(scrollableList)
                );
                scrollableList.removeEventListener('scrollend', () =>
                    resetScrollingList(scrollableList)
                );
            };
        }
    }, []);

    const getTruncateValue = (option = '') => {
        const optionLength = option.length;
        if (optionLength >= 24) {
            let value = '';
            for (let i = 0; i < optionLength; i += 1) {
                value += option[i];
                if (i >= 24) {
                    value = `${customOptions ? name : value}...`;
                    break;
                }
            }
            return value.toLowerCase();
        }
        return option.toLowerCase();
    };

    const onPillsClick = (value, id = '', url = '') => {
        /* istanbul ignore next */
        if (id === ProductConstants.EXTENDEDSIZERANGE && url) {
            window && window.location.assign(url);
        } else if (onOptionClick) {
            onOptionClick(productOptions, null, value);
        }
    };
    const isExtendedSizeEligible =
        productOptions.id === productDimensions.SIZE_RANGE && extendedSizeDetails?.length > 0;
    /* istanbul ignore next */
    if (isExtendedSizeEligible) {
        extendedSizeDetails?.map((data) => {
            productOptions.options.push({
                value: data.linkText,
                id: ProductConstants.EXTENDEDSIZERANGE,
                url: data.link,
            });
        });
    }

    /* istanbul ignore next */
    const replaceMatchingOptions = (data, a) => {
        const newData = { ...data };
        newData.options = newData?.options?.map((option) => {
            const matchingOption = a?.options?.find((optionA) => optionA.value === option.value);
            return matchingOption ? { ...matchingOption, ...option } : option;
        });
        return newData;
    };

    /* istanbul ignore next */
    const updateChevronVisiility = (scrollableList) => {
        if (scrollableList) {
            const maxScrollLeft = scrollableList.scrollWidth - scrollableList.clientWidth - 12;
            setIsLeftOverflow(scrollableList.scrollLeft > 0);
            setIsRightOverflow(scrollableList.scrollLeft < maxScrollLeft);
        }
    };

    /* istanbul ignore next */
    const resetScrollingList = (scrollableList) => {
        const maxScrollLeft = scrollableList.scrollWidth - scrollableList.clientWidth;
        if (scrollableList.scrollLeft < 0) scrollableList.scrollLeft = 0;
        if (scrollableList.scrollLeft > maxScrollLeft) scrollableList.scrollLeft = maxScrollLeft;
    };

    /* istanbul ignore next */
    const onLeftChevronClick = () => {
        if (listRef) {
            const newScrollPosition = listRef.scrollLeft - 0.75 * listRef.clientWidth;
            listRef.scrollLeft = newScrollPosition;
        }
    };

    /* istanbul ignore next */
    const onRightChevronClick = () => {
        if (listRef) {
            const newScrollPosition = listRef.scrollLeft + 0.75 * listRef.clientWidth;
            listRef.scrollLeft = newScrollPosition;
        }
    };

    /* istanbul ignore next */
    congruentPPsDetails?.map((element) => {
        const iscongruentPPEligible =
            productOptions.name === element.name && congruentPPsDetails?.length > 0;
        if (iscongruentPPEligible) {
            const updatedData = replaceMatchingOptions(element, productOptions);
            productOptions = updatedData;
        }
    });

    const pillContainerStyleForMobile =
        congruentPPsDetails?.length > 0
            ? dt(['smOnly:relative', 'smOnly:w-full', 'smOnly:text-center'])
            : '';

    const mobileColorCaraouselStyle =
        congruentPPsDetails?.length > 0
            ? dt([
                  'smOnly:flex',
                  'smOnly:flex-nowrap',
                  'smOnly:overflow-y-scroll',
                  'smOnly:scroll-smooth',
                  'smOnly:-mx-6',
                  'smOnly:w-[calc(100%+48px)]',
                  'smOnly:pl-6',
                  'smOnly:pr-6',
              ])
            : '';

    return (
        <section
            className={`${cx('pillsContainer')} ${pillContainerStyleForMobile}`}
            data-automation-id="productOptionPillsBlock"
        >
            {congruentPPsDetails?.length > 0 && isLeftOverflow && isMobile && !inLandscapeMode && (
                <div className={cx('chevronContainerLeft')}>
                    <button className={cx('chevronButton', 'left')} onClick={onLeftChevronClick}>
                        <Icon iconType="svg">
                            <LeftChevron />
                        </Icon>
                    </button>
                </div>
            )}
            <ul
                id={productOptions.id}
                className={`${cx({
                    pills: true,
                    noScrollBar: isMobile,
                })} ${mobileColorCaraouselStyle}`}
                ref={(el) => {
                    if (isMobile && congruentPPsDetails?.length > 0) listRef = el;
                }}
            >
                {productOptions.options &&
                    productOptions.options.map(
                        ({ name, value, availableInStock, availabilityMessage, id, url }) => {
                            const isOptionSelected = customOptions
                                ? name && name.toLowerCase() === selectedOption.toLowerCase()
                                : value && value.toLowerCase() === selectedOption.toLowerCase();
                            const isOOS = availableInStock === false;
                            return (
                                <li
                                    key={`${value}-${clientRendering ? 'c' : 's'}-${
                                        availabilityMessage ? 'a' : ''
                                    }`}
                                    className={`${cx('pillsBlock')} ${
                                        congruentPPsDetails?.length > 0 &&
                                        dt(['smOnly:last:!mr-0', 'smOnly:mt-1', 'smOnly:mb-1'])
                                    }`}
                                    data-option={customOptions ? name : value}
                                >
                                    <button
                                        onClick={() => onPillsClick(value, id, url)}
                                        className={`${cx({
                                            btn: true,
                                            btnDefault: true,
                                            btnNunSelected: checkSizeWarning && !isOOS,
                                            btnOos: isOOS,
                                            btnSelected: isOptionSelected,
                                            btnOosSelected: isOOS && isOptionSelected,
                                        })} ${
                                            congruentPPsDetails?.length > 0 &&
                                            dt(['smOnly:whitespace-nowrap'])
                                        }`}
                                    >
                                        {getTruncateValue(customOptions ? name : value)}
                                    </button>
                                </li>
                            );
                        }
                    )}
            </ul>
            {congruentPPsDetails?.length > 0 && isRightOverflow && isMobile && !inLandscapeMode && (
                <div className={cx('chevronContainerRight')} onClick={onRightChevronClick}>
                    <button className={cx('chevronButton', 'right')}>
                        <Icon iconType="svg">
                            <RightChevron />
                        </Icon>
                    </button>
                </div>
            )}
        </section>
    );
}

ProductOptionPills.propTypes = {
    productOptions: PropTypes.object,
    selectedOption: PropTypes.string,
    onOptionClick: PropTypes.func,
    clientRendering: PropTypes.bool,
    customOptions: PropTypes.bool,
    checkSizeWarning: PropTypes.bool,
    isMobile: PropTypes.bool,
};

ProductOptionPills.defaultProps = {
    productOptions: {},
    selectedOption: '',
    onOptionClick: null,
    clientRendering: false,
    customOptions: false,
    checkSizeWarning: false,
    isMobile: false,
};

export default ProductOptionPills;
